import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { cs_CZ, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import cs from '@angular/common/locales/cs';
import { initIdentityFactory } from '@domains/identity/domain/application-services/identity-init.factory';
import { AuthorizationFacade } from '@domains/identity/domain/application-services/authorization.facade';
import { NgxPermissionsModule } from 'ngx-permissions';
import { PageLoaderComponent } from '@domains/shared/feature/components/page-loader/page-loader.component';
import { BpCollapseComponent } from '@domains/shared/feature/components/bp-collapse/bp-collapse.component';
import { ErrorInterceptor } from '@domains/identity/domain/interceptor/error-interceptor.interceptor';
import { TokenInterceptor } from '@domains/identity/domain/interceptor/token-interceptor.interceptor';
import { LanguageInterceptor } from '@domains/shared/domain/interceptor/language-interceptor.interceptor';
import { defaultUiLanguage } from '@domains/shared/domain/models/language.model';
import { prefersReducedMotion } from '@domains/shared/domain/utils/motion';
import { OVERLAY_DEFAULT_CONFIG, OverlayContainer, OverlayKeyboardDispatcher, OverlayOutsideClickDispatcher } from '@angular/cdk/overlay';
import { HtmlOverlayContainerService } from '@domains/shared/domain/application-service/html-overlay-container.service';
import { HtmlOverlayKeyboardDispatcher } from '@domains/shared/domain/application-service/html-overlay-keyboard-dispatcher.service';
import { HtmlOverlayOutsideClickDispatcher } from '@domains/shared/domain/application-service/html-overlay-outside-click-dispatcher.service';

registerLocaleData(cs);
// Bez registrace locale by anglické datum a čísla padaly zpět na cs.
registerLocaleData(en);

/**
 * Cache-busting překladů.
 *
 * `outputHashing` v angular.json hashuje jen bundly, ne soubory kopírované přes `assets`
 * glob. `assets/i18n/*.json` mají tedy po nasazení pořád stejnou URL a nginx na tegleru
 * je servíruje s `max-age=86400` — prohlížeč pak i den drží staré překlady a nová
 * obrazovka se vykreslí s holými i18n klíči.
 *
 * Otisk se generuje při každém načtení aplikace, ne z hashe bundlu. Hash bundlu by
 * nestačil: release, který mění jen překlady nebo jen lazy chunk, nechá `main.js`
 * bajtově stejný a překlady by se znovu nenačetly.
 *
 * Cena je jeden request na jazyk při načtení stránky (~29 kB). Až bude nginx servírovat
 * `/assets/` s `no-cache`, může tohle pryč — revalidace přes ETag zvládne totéž levněji
 * a pokryje i obrázky.
 */
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, '/assets/i18n/', `.json?v=${Date.now()}`);
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BpCollapseComponent,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    // Kdo má v systému zapnuté omezení pohybu, dostane aplikaci bez animací —
    // včetně těch od ng-zorro (drawer, modal, collapse). Vyhodnocuje se jednou při
    // načtení modulu, změna nastavení v OS se tedy projeví až po obnovení stránky.
    BrowserAnimationsModule.withConfig({
      disableAnimations: prefersReducedMotion(),
    }),
    PageLoaderComponent,
    NgxPermissionsModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: defaultUiLanguage,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    // Náhrada HttpClientModule, který je od Angularu 18 deprecated (EL-2324).
    // `withInterceptorsFromDi` je nutné — interceptory jsou třídy registrované přes
    // HTTP_INTERCEPTORS níž; bez něj by se tiše přestaly volat (token, jazyk, chyby).
    provideHttpClient(withInterceptorsFromDi()),
    TranslatePipe,
    { provide: NZ_I18N, useValue: cs_CZ },
    // Potvrzení (nz-popconfirm) vezme fokus na Storno a po zavření ho vrátí spouštěči
    // (EL-2293). Bez toho se z klávesnice otevřelo, ale na OK ani Storno Tab nedošel —
    // vrstva visí na konci stránky. Storno, ne OK: Enter omylem nic nesmaže.
    { provide: NZ_CONFIG, useValue: { popconfirm: { nzAutofocus: 'cancel' } } },
    // Plovoucí vrstvy visí pod <html>, mimo <body> invertované tmavým motivem (EL-2271).
    { provide: OverlayContainer, useClass: HtmlOverlayContainerService },
    // …a do toho kontejneru je musí CDK opravdu vkládat. Od CDK 20 je výchozí nativní
    // `popover` v top layer, kam filtr kontejneru (tmavý motiv) nedosáhne: nabídky,
    // zásuvky i dialogy zůstaly v tmavém motivu světlé a obrázky v nich převrácené (EL-2388).
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    // …a klávesy pro ně se proto čtou na <html>, jinak Escape z nabídky nebo dialogu
    // s fokusem uvnitř k vrstvě nedojde (EL-2293).
    { provide: OverlayKeyboardDispatcher, useClass: HtmlOverlayKeyboardDispatcher },
    // …a stejně kliknutí „mimo vrstvu": jinak nabídku otevřenou v zásuvce nezavře
    // klik jinam do zásuvky a zakrývá její tlačítka (EL-2342).
    { provide: OverlayOutsideClickDispatcher, useClass: HtmlOverlayOutsideClickDispatcher },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initIdentityFactory,
      deps: [AuthorizationFacade],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
